import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { completedItemIdSet, itemAccessList, requiredCompletionPercent } from '@drivingdoc/shared-types';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SerializedEditorState } from 'lexical';
import { useParams } from 'react-router';

import {
  getLearningCourseOptions,
  getLearningCourseQueryKey,
  updateLearningProgressMutation,
} from '@drivingdoc/api-client/generated/react-query';
import type {
  CurriculumLesson,
  ItemProgress,
  LearningCourse,
  LearningItemAccess,
  LearningQuiz,
} from '@drivingdoc/api-client/generated/types';

import RichTextViewer from 'components/Editor/RichTextViewer';
import { PageContainer } from 'components/PageContainer';

import QuizRunner from './QuizRunner';

type PlayerItem = CurriculumLesson | LearningQuiz;

const HEARTBEAT_INTERVAL_SECONDS = 15;
const MAX_HEARTBEAT_DELTA_SECONDS = 90;

function formatVideoTime(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
}

function lessonVideoUrl(content: Record<string, unknown>): string | undefined {
  return typeof content.videoUrl === 'string' && content.videoUrl ? content.videoUrl : undefined;
}

function lessonRichText(content: Record<string, unknown>): SerializedEditorState | null {
  if (!content.root || typeof content.root !== 'object') return null;
  const rest = { ...content };
  delete rest.s3Key;
  delete rest.s3Bucket;
  delete rest.videoUrl;
  delete rest.durationSeconds;
  return rest as unknown as SerializedEditorState;
}

function flattenItems(course: LearningCourse | undefined): PlayerItem[] {
  return course?.curriculum.modules.flatMap((module) => module.items) ?? [];
}

function computeItemAccess(
  items: PlayerItem[],
  progress: ItemProgress[],
  bypassMinTime: boolean,
): LearningItemAccess[] {
  return itemAccessList(items, completedItemIdSet(progress), bypassMinTime);
}

function computeProgressPercent(items: PlayerItem[], progress: ItemProgress[]): number {
  const required = items.filter((item) => item.required).map((item) => item.id);
  return requiredCompletionPercent(required, completedItemIdSet(progress));
}

function applyProgressUpdate(
  course: LearningCourse,
  progress: ItemProgress,
  courseCompletedAt: string | null | undefined,
  dailyLimitReached: boolean,
  dailySecondsRemaining: number,
): LearningCourse {
  const progressList = [...course.progress];
  const index = progressList.findIndex((row) => row.itemId === progress.itemId);
  if (index >= 0) progressList[index] = progress;
  else progressList.push(progress);
  const items = flattenItems(course);
  return {
    ...course,
    progress: progressList,
    progressPercent: computeProgressPercent(items, progressList),
    itemAccess: computeItemAccess(items, progressList, course.bypassMinTime === true),
    completedAt: courseCompletedAt ?? course.completedAt,
    dailyLimitReached,
    dailySecondsRemaining,
  };
}

export default function CoursePlayerPage() {
  const { courseId = '' } = useParams();
  const client = useQueryClient();
  const courseQueryKey = getLearningCourseQueryKey({ path: { courseId } });
  const course = useQuery({
    ...getLearningCourseOptions({ path: { courseId } }),
    enabled: Boolean(courseId),
    // Curriculum is enrollment-pinned; heartbeats patch progress into the cache.
    staleTime: 5 * 60_000,
  });
  const progress = useMutation({
    ...updateLearningProgressMutation(),
    onSuccess: (data) => {
      client.setQueryData<LearningCourse>(courseQueryKey, (current) =>
        current
          ? applyProgressUpdate(
              current,
              data.progress,
              data.courseCompletedAt,
              data.dailyLimitReached,
              data.dailySecondsRemaining,
            )
          : current,
      );
    },
  });
  const modules = course.data?.curriculum.modules ?? [];
  const items = useMemo(() => flattenItems(course.data), [course.data]);
  const itemAccess = course.data?.itemAccess;
  const accessById = useMemo(
    () => new Map((itemAccess ?? []).map((entry) => [entry.itemId, entry.unlocked])),
    [itemAccess],
  );
  const isUnlocked = (itemId: string) => (itemAccess?.length ? (accessById.get(itemId) ?? false) : true);
  const [selectedId, setSelectedId] = useState('');
  const selected =
    items.find((item) => item.id === selectedId) ?? items.find((item) => isUnlocked(item.id)) ?? items[0];
  const selectedModule = modules.find((module) => module.items.some((item) => item.id === selected?.id));
  const selectedPath = selected && selectedModule ? `${selectedModule.title}/${selected.title}` : selected?.title;
  const selectedUnlocked = selected ? isUnlocked(selected.id) : false;
  const completedIds = useMemo(() => completedItemIdSet(course.data?.progress ?? []), [course.data?.progress]);
  const [mediaRefreshAttempted, setMediaRefreshAttempted] = useState(false);
  const [forceMediaRefresh, setForceMediaRefresh] = useState(false);
  /** Keep the playing signed URL stable across progress-only cache updates / refetches. */
  const [playbackMedia, setPlaybackMedia] = useState<{ itemId: string; url: string } | null>(null);
  /** Seat time for video lessons only accrues while the media element is playing. */
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const bypassMinTime = course.data?.bypassMinTime === true;
  /** Video scrubbing is locked until the student has watched a lesson's video to the end once. */
  const [hasWatchedFully, setHasWatchedFully] = useState(bypassMinTime);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  /** Tracks the video's actual position independent of React's render cycle, so the scrub-lock
   * always snaps back to a live position rather than a state value that can lag behind rapid
   * seek attempts fired before a re-render lands. */
  const allowedVideoTimeRef = useRef(0);
  const progressMutateRef = useRef(progress.mutate);
  progressMutateRef.current = progress.mutate;

  useEffect(() => {
    if (videoRef.current) videoRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted;
  }, [isMuted]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(document.fullscreenElement === videoRef.current);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!items.length) return;
    if (selectedId && isUnlocked(selectedId)) return;
    const firstUnlocked = items.find((item) => isUnlocked(item.id));
    if (firstUnlocked) setSelectedId(firstUnlocked.id);
  }, [items, itemAccess, selectedId]);

  useEffect(() => {
    setMediaRefreshAttempted(false);
    setForceMediaRefresh(false);
    setIsVideoPlaying(false);
    setHasWatchedFully(bypassMinTime);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    allowedVideoTimeRef.current = 0;
  }, [selected?.id, course.data?.curriculumVersionId, bypassMinTime]);

  useLayoutEffect(() => {
    if (!selected?.id) return;
    contentRef.current?.scrollIntoView({ block: 'start' });
  }, [selected?.id]);

  const selectedLessonUrl =
    selected?.type === 'lesson' ? lessonVideoUrl(selected.content as Record<string, unknown>) : undefined;
  const selectedProgress = course.data?.progress.find((item) => item.itemId === selected?.id);

  useEffect(() => {
    // The `ended` event unlocks scrubbing immediately for responsiveness, but the server's
    // watchedFully (derived from anti-cheat-clamped seat time, not a spoofable client event) is
    // the source of truth once it's available — adopt it as soon as it confirms a full watch-through.
    if (selectedProgress?.watchedFully) setHasWatchedFully(true);
  }, [selectedProgress?.watchedFully]);

  const lessonMinSeconds = selected?.type === 'lesson' && !bypassMinTime ? selected.minTimeSeconds : 0;
  const quizMinSeconds = selected?.type === 'quiz' && !bypassMinTime ? selected.minTimeSeconds : 0;
  const lessonTimeComplete =
    selectedProgress?.status === 'completed' ||
    (lessonMinSeconds > 0 && (selectedProgress?.timeSpentSeconds ?? 0) >= lessonMinSeconds);
  const quizTimeComplete =
    selected?.type === 'quiz' && (quizMinSeconds <= 0 || (selectedProgress?.timeSpentSeconds ?? 0) >= quizMinSeconds);

  useEffect(() => {
    if (!selected || selected.type !== 'lesson') {
      setPlaybackMedia(null);
      return;
    }
    if (!selectedLessonUrl) return;
    setPlaybackMedia((current) => {
      if (!forceMediaRefresh && current?.itemId === selected.id) return current;
      return { itemId: selected.id, url: selectedLessonUrl };
    });
    if (forceMediaRefresh) setForceMediaRefresh(false);
  }, [selected?.id, selected?.type, selectedLessonUrl, forceMediaRefresh]);

  useEffect(() => {
    if (!selected || !selectedUnlocked) return;
    if (selected.type !== 'lesson' && selected.type !== 'quiz') return;
    const itemId = selected.id;
    progressMutateRef.current({ path: { courseId, itemId }, body: { event: 'view' } });
  }, [courseId, selected?.id, selected?.type, selectedUnlocked]);

  useEffect(() => {
    if (!selected || !selectedUnlocked) return;
    if (course.data?.dailyLimitReached) return;
    if (selected.type === 'lesson') {
      if (lessonTimeComplete) return;
      const hasVideo = Boolean(selectedLessonUrl);
      // Video lessons: credit only while playing. Text-only lessons: credit while open.
      if (hasVideo && !isVideoPlaying) return;
    } else if (selected.type === 'quiz') {
      if (selectedProgress?.status === 'completed' || quizTimeComplete) return;
    } else {
      return;
    }

    const itemId = selected.id;
    let accruedSeconds = 0;
    let lastTickMs = Date.now();

    const flush = (forcePartial: boolean) => {
      const now = Date.now();
      accruedSeconds += (now - lastTickMs) / 1000;
      lastTickMs = now;
      const wholeSeconds = Math.floor(accruedSeconds);
      if (wholeSeconds <= 0) return;
      if (!forcePartial && wholeSeconds < HEARTBEAT_INTERVAL_SECONDS) return;
      const delta = Math.min(wholeSeconds, MAX_HEARTBEAT_DELTA_SECONDS);
      accruedSeconds -= delta;
      progressMutateRef.current({
        path: { courseId, itemId },
        body: { event: 'heartbeat', deltaTimeSpentSeconds: delta },
      });
    };

    const timer = window.setInterval(() => flush(false), 1000);
    return () => {
      window.clearInterval(timer);
      // Pause / ended / item switch: credit partial time.
      flush(true);
    };
  }, [
    courseId,
    selected?.id,
    selected?.type,
    selectedUnlocked,
    selectedLessonUrl,
    isVideoPlaying,
    lessonTimeComplete,
    quizTimeComplete,
    selectedProgress?.status,
    course.data?.dailyLimitReached,
  ]);

  if (course.isPending) {
    return (
      <PageContainer title="Course">
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress aria-label="Loading course" />
        </Box>
      </PageContainer>
    );
  }
  if (course.isError) {
    return (
      <PageContainer title="Course">
        <Alert severity="error">The course could not be loaded. Please refresh and try again.</Alert>
      </PageContainer>
    );
  }
  if (!selected) return <PageContainer title="Course">No lessons or quizzes are available.</PageContainer>;
  const videoUrl = selected.type === 'lesson' && playbackMedia?.itemId === selected.id ? playbackMedia.url : undefined;
  const richText = selected.type === 'lesson' ? lessonRichText(selected.content as Record<string, unknown>) : null;
  const displayedTimeSeconds =
    lessonMinSeconds > 0
      ? Math.min(selectedProgress?.timeSpentSeconds ?? 0, lessonMinSeconds)
      : (selectedProgress?.timeSpentSeconds ?? 0);

  const refreshSignedMedia = () => {
    if (mediaRefreshAttempted) return;
    setMediaRefreshAttempted(true);
    void client.invalidateQueries({ queryKey: courseQueryKey }).then(() => {
      setForceMediaRefresh(true);
    });
  };

  const dailyLimitReached = course.data?.dailyLimitReached === true;
  const dailySecondsRemaining = course.data?.dailySecondsRemaining;
  const showDailyLimitWarning =
    !dailyLimitReached &&
    typeof dailySecondsRemaining === 'number' &&
    dailySecondsRemaining > 0 &&
    dailySecondsRemaining <= 600;

  return (
    <PageContainer title="Course">
      <Stack spacing={2}>
        <LinearProgress variant="determinate" value={course.data?.progressPercent ?? 0} />
        {course.data?.completedAt && <Alert severity="success">Course complete.</Alert>}
        {showDailyLimitWarning && (
          <Alert severity="warning">You have 10 minutes of online coursework time left today.</Alert>
        )}
        {dailyLimitReached ? (
          <Alert severity="info">
            You&apos;ve reached today&apos;s 3-hour limit for online coursework. More time will be available tomorrow.
          </Alert>
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Stack
              component="nav"
              aria-label="Course content"
              spacing={1}
              sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}
            >
              {modules.map((module) => (
                <Accordion
                  key={module.id}
                  defaultExpanded={module.items.some((item) => item.id === selected.id)}
                  disableGutters
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider', '&::before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">{module.title}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {module.items.map((item) => {
                        const unlocked = isUnlocked(item.id);
                        const completed = completedIds.has(item.id);
                        const current = item.id === selected.id;
                        return (
                          <Button
                            key={item.id}
                            variant={current ? 'contained' : 'text'}
                            disabled={!unlocked}
                            startIcon={
                              !unlocked ? (
                                <LockOutlinedIcon fontSize="small" />
                              ) : completed ? (
                                <CheckCircleOutlineIcon fontSize="small" />
                              ) : undefined
                            }
                            onClick={() => setSelectedId(item.id)}
                            aria-current={current ? 'page' : undefined}
                            data-completed={completed ? 'true' : 'false'}
                            sx={{
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              color: completed && !current ? 'text.secondary' : undefined,
                              fontWeight: current ? 700 : undefined,
                            }}
                          >
                            {item.title}
                          </Button>
                        );
                      })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
            <Box ref={contentRef} sx={{ width: '100%', minWidth: 0, scrollMarginTop: 80 }} data-testid="course-content">
              {!selectedUnlocked ? (
                <Alert severity="info" sx={{ width: '100%' }}>
                  Complete the previous lesson or pass the previous quiz (80% or better) to unlock this item.
                </Alert>
              ) : selected.type === 'quiz' ? (
                <QuizRunner
                  courseId={courseId}
                  quiz={selected}
                  pathLabel={selectedPath}
                  result={course.data?.quizResults.find((result) => result.itemId === selected.id)}
                  timeSpentSeconds={selectedProgress?.timeSpentSeconds ?? 0}
                  bypassMinTime={bypassMinTime}
                />
              ) : (
                <Card variant="outlined" sx={{ width: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {selectedPath}
                    </Typography>
                    {videoUrl ? (
                      <Stack spacing={1}>
                        <Box
                          component="video"
                          ref={videoRef}
                          playsInline
                          src={videoUrl}
                          tabIndex={0}
                          onClick={() => {
                            if (isVideoPlaying) videoRef.current?.pause();
                            else void videoRef.current?.play();
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== ' ' && event.key !== 'Enter') return;
                            event.preventDefault();
                            if (isVideoPlaying) videoRef.current?.pause();
                            else void videoRef.current?.play();
                          }}
                          onPlay={() => setIsVideoPlaying(true)}
                          onPlaying={() => setIsVideoPlaying(true)}
                          onPause={() => setIsVideoPlaying(false)}
                          onEnded={() => {
                            setIsVideoPlaying(false);
                            setHasWatchedFully(true);
                          }}
                          onLoadedMetadata={(event) => setVideoDuration(event.currentTarget.duration || 0)}
                          onTimeUpdate={(event) => {
                            const currentTime = event.currentTarget.currentTime;
                            allowedVideoTimeRef.current = currentTime;
                            setVideoCurrentTime(currentTime);
                          }}
                          onSeeking={(event) => {
                            // Before a full watch-through, ignore all manual seeking (forward or back).
                            // Snap back using the ref (always current) rather than React state, which can
                            // lag behind rapid seek attempts fired before a re-render lands.
                            if (hasWatchedFully) return;
                            event.currentTarget.currentTime = allowedVideoTimeRef.current;
                          }}
                          onError={refreshSignedMedia}
                          sx={{ width: '100%', maxHeight: '70vh', bgcolor: 'common.black' }}
                        />
                        <Stack direction="row" spacing={2} alignItems="center">
                          <IconButton
                            aria-label={isVideoPlaying ? 'Pause' : 'Play'}
                            onClick={() => {
                              if (isVideoPlaying) videoRef.current?.pause();
                              else void videoRef.current?.play();
                            }}
                          >
                            {isVideoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                          </IconButton>
                          <Tooltip
                            title={
                              hasWatchedFully
                                ? ''
                                : 'Watch the entire video before you can jump to a different section.'
                            }
                          >
                            <Box component="span" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                              <Slider
                                size="small"
                                disabled={!hasWatchedFully}
                                value={videoCurrentTime}
                                max={videoDuration || 0}
                                onChange={(_event, value) => {
                                  const nextTime = Array.isArray(value) ? value[0] : value;
                                  if (videoRef.current) videoRef.current.currentTime = nextTime;
                                  setVideoCurrentTime(nextTime);
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
                            {formatVideoTime(videoCurrentTime)} / {formatVideoTime(videoDuration)}
                          </Typography>
                          <IconButton
                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                            onClick={() => setIsMuted((current) => !current)}
                          >
                            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                          </IconButton>
                          <Slider
                            size="small"
                            value={isMuted ? 0 : volume}
                            min={0}
                            max={1}
                            step={0.05}
                            sx={{ width: 80 }}
                            aria-label="Volume"
                            onChange={(_event, value) => {
                              const nextVolume = Array.isArray(value) ? value[0] : value;
                              setVolume(nextVolume);
                              if (nextVolume > 0 && isMuted) setIsMuted(false);
                            }}
                          />
                          <IconButton
                            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                            onClick={() => {
                              if (document.fullscreenElement) void document.exitFullscreen();
                              else void videoRef.current?.requestFullscreen();
                            }}
                          >
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                          </IconButton>
                        </Stack>
                      </Stack>
                    ) : richText ? (
                      <RichTextViewer value={richText} namespace={`lesson-${selected.id}`} />
                    ) : (
                      <Alert severity="warning">This lesson has no playable media yet.</Alert>
                    )}
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      Time recorded: {displayedTimeSeconds}s
                      {lessonMinSeconds > 0 ? ` of ${lessonMinSeconds}s minimum` : ''}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
}
